import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Moon } from 'lucide-react';

const Activities = ({ activities, moonData }) => {
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">🎯 Activities & Adventures</h2>
      {activities.map((activity, idx) => (
        <div key={idx} className="bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-lg shadow-md p-4 border border-orange-100 dark:border-gray-700">
          <h3 className="font-semibold text-lg mb-2 dark:text-gray-100">{activity.name}</h3>
          <p className="text-gray-700 dark:text-gray-300 mb-1">{activity.details}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 italic">{activity.note}</p>
          
          {/* Special formatting for drone fishing */}
          {activity.equipment && (
            <div className="mt-4 pt-4 border-t border-orange-200 dark:border-gray-600">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    🎣 Equipment Needed
                  </h4>
                  <ul className="text-xs text-gray-800 dark:text-gray-200 space-y-1">
                    {activity.equipment.map((item, eidx) => (
                      <li key={eidx}>• {item}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                  <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                    💡 Pro Tips
                  </h4>
                  <ul className="text-xs text-gray-800 dark:text-gray-200 space-y-1">
                    {activity.tips.map((tip, tidx) => (
                      <li key={tidx}>• {tip}</li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Tuna-specific section */}
              {activity.tunaSpecific && (
                <div className="mt-3 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg p-3 border border-blue-600">
                  <h4 className="font-medium text-sm text-white mb-2">
                    {activity.tunaSpecific.title}
                  </h4>
                  <ul className="text-xs text-blue-50 space-y-1">
                    {activity.tunaSpecific.details.map((detail, didx) => (
                      <li key={didx}>• {detail}</li>
                    ))}
                  </ul>
                  <div className="mt-2 p-2 bg-blue-600/50 rounded">
                    <p className="text-xs font-semibold text-white">
                      🎯 June = PRIME TUNA TIME at Cape San Blas!
                    </p>
                  </div>
                </div>
              )}
              
              <div className="mt-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
                <h4 className="font-medium text-sm text-gray-900 mb-2 flex items-center">
                  📋 Regulations & Safety
                </h4>
                <ul className="text-xs text-gray-800 space-y-1">
                  {activity.regulations.map((reg, ridx) => (
                    <li key={ridx}>• {reg}</li>
                  ))}
                </ul>
              </div>
              
              <div className="mt-3 p-3 bg-gradient-to-r from-blue-500 to-orange-500 rounded-lg border border-blue-600">
                <p className="text-sm font-medium text-white mb-1">🎯 Prime Fishing Zones</p>
                <p className="text-xs text-blue-50">
                  <strong>Nearshore (500-1000ft):</strong> King Mackerel, Spanish, Cobia at the second sandbar (20-30ft)<br/>
                  <strong>Offshore (1500-2000ft):</strong> Blackfin Tuna in the blue water (60-100ft) - look for temperature breaks!
                </p>
              </div>
              
              <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded border border-orange-200 dark:border-orange-800">
                <p className="text-xs text-orange-800 dark:text-orange-200">
                  💨 <strong>Pre-flight check:</strong> Use the Windy.com maps in Weather section for real-time wind & wave conditions!
                </p>
              </div>
            </div>
          )}
        </div>
      ))}
      
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
        <h3 className="font-semibold mb-2 flex items-center dark:text-gray-100">
          <Moon className="w-5 h-5 mr-2" />
          Moon Phase Special
        </h3>
        <p className="text-sm text-gray-900 dark:text-gray-200">
          June 25-26 is NEW MOON - perfect for stargazing! The darkest nights of your trip.
        </p>
        {moonData?.current && (
          <p className="text-xs text-gray-800 dark:text-gray-300 mt-2">
            Current phase: {moonData.current.emoji} {moonData.current.name} ({moonData.current.illumination}% illuminated)
          </p>
        )}
      </div>
    </div>
  );
};

Activities.propTypes = {
  activities: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    details: PropTypes.string,
    note: PropTypes.string,
    equipment: PropTypes.arrayOf(PropTypes.string),
    tips: PropTypes.arrayOf(PropTypes.string),
    regulations: PropTypes.arrayOf(PropTypes.string),
    tunaSpecific: PropTypes.shape({
      title: PropTypes.string,
      details: PropTypes.arrayOf(PropTypes.string)
    })
  })).isRequired,
  moonData: PropTypes.shape({
    current: PropTypes.shape({
      emoji: PropTypes.string,
      name: PropTypes.string,
      illumination: PropTypes.number
    })
  })
};

export default memo(Activities);